import React, { useContext, useEffect, useRef, useState } from "react";
import "./AddTaskModal.css";
import { IoCloseOutline, IoDocumentTextOutline } from "react-icons/io5";
import { TbStack2 } from "react-icons/tb";
import { useLanguage } from "../../../LanguageProvider";
import DatePicker from "../../../components/DatePicker/DatePicker";
import TimePicker from "../../../components/TimePicker/TimePicker";
import AddModalInputField from "../../../components/AddModalInputField/AddModalInputField";
import { GeneralContext } from "../../../GeneralProvider";
import { useToaster } from "../../../ToasterProvider";
import { GrAttachment } from "react-icons/gr";
import { CgCloseO } from "react-icons/cg";
import Dropdown from "../../../components/Dropdown/Dropdown";

const convertTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [timePart, meridian] = timeStr.split(" ");
  let [hours, minutes] = timePart.split(":").map(Number);

  if (meridian === "PM" && hours !== 12) {
    hours += 12;
  }
  if (meridian === "AM" && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
};

export default function AddTaskModal({
  isOpen,
  selectedPanelId,
  boardId,
  onClose,
}) {
  const { getTranslation } = useLanguage();
  const { toastWarning } = useToaster();
  const { userId, selectedProjectID, handleAddNewTask, subProjects } =
    useContext(GeneralContext);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskSubtitle, setTaskSubtitle] = useState("");
  const [taskSubProject, setTaskSubProject] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef();

  // Dropdown seçeneklerini oluşturuyoruz; ilk seçenek boş yani "Project'siz devam et"
  const dropdownOptions = [
    { value: "", label: getTranslation("continueWithoutProject") },
    ...(subProjects && Array.isArray(subProjects) ? subProjects : []),
  ];

  useEffect(() => {
    if (startDate && !endDate) {
      setEndDate(startDate);
    }
  }, [startDate]);

  useEffect(() => {
    if (startTime && !endTime) {
      const [hourStr, minuteStr] = startTime.split(":");
      let hour = parseInt(hourStr, 10);
      let minute = parseInt(minuteStr, 10);

      // 2 saat (120 dakika) ekle
      let totalMinutes = hour * 60 + minute + 240;

      // Maksimum dakika: 23*60 + 45 = 1425 (23:45)
      const maxMinutes = 23 * 60 + 45;
      if (totalMinutes > maxMinutes) {
        totalMinutes = maxMinutes;
      }

      // Yeni saat ve dakika hesapla
      const newHour = Math.floor(totalMinutes / 60);
      const newMinute = totalMinutes % 15 === 0 ? (totalMinutes % 60) : (Math.ceil((totalMinutes % 60) / 15) * 15) % 60;

      const pad = (n) => String(n).padStart(2, "0");
      setEndTime(`${pad(newHour)}:${pad(newMinute)}`);
    }
  }, [startTime]);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (
      taskTitle.trim() === "" ||
      !startDate ||
      !startTime ||
      !endDate ||
      !endTime
    ) {
      toastWarning(getTranslation("pleaseFillAllFields"));
      return;
    }

    if (startDate === endDate && startTime && endTime) {
      const startTotalMinutes = convertTimeToMinutes(startTime);
      const endTotalMinutes = convertTimeToMinutes(endTime);

      if (endTotalMinutes < startTotalMinutes) {
        toastWarning(getTranslation("endTimeCannotBeBeforeStartTime"));
        return; 
      }
    }

    const formatDateTime = (date, time) => {
      const d = new Date(date);
      const formattedDate = `${d.getFullYear()}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const [timePart, meridian] = time.split(" ");
      let [hours, minutes] = timePart.split(":");

      if (meridian === "PM" && hours !== "12") hours = parseInt(hours, 10) + 12;
      if (meridian === "AM" && hours === "12") hours = "00";

      return `${formattedDate} ${String(hours).padStart(2, "0")}:${minutes}:00`;
    };

    const taskStart = formatDateTime(startDate, startTime);
    const taskEnd = formatDateTime(endDate, endTime);
    const attachment = selectedFiles?.length > 0 ? selectedFiles : [];

    const data = {
      title: taskTitle,
      panelId: selectedPanelId,
      boardId: boardId,
      subTitle: taskSubtitle,
      description: taskDescription,
      taskStart: taskStart,
      taskEnd: taskEnd,
      projectId: selectedProjectID,
      subProjectId: taskSubProject,
      attachment: attachment,
    };

    handleAddNewTask(data);
    onClose();
    setTaskTitle("");
    setTaskSubtitle("");
    setTaskDescription("");
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
    setSelectedFiles([]);
    setTaskSubProject("");
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1]; // "data:image/jpeg;base64," kısmını kaldırıyoruz
        const extension = file.name.split(".").pop();
        const title =
          file?.name?.substring(0, file.name.lastIndexOf(".")) || file?.name;
        setSelectedFiles((prevFiles) => [
          ...prevFiles,
          { file: base64String, extension: extension, title: title },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((file, i) => i !== index));
  };

  const handleClose = () => {
    onClose();
    setTaskTitle("")
    setTaskSubtitle("")
    setTaskSubProject("")
    setTaskDescription("")
    setStartDate("")
    setStartTime("")
    setEndDate("")
    setEndTime("")
    setSelectedFiles([])
  }

  return (
    <div className="add-task-modal-overlay">
      <div className="add-task-modal-container">
        <IoCloseOutline
          className="add-task-modal-button close"
          onClick={handleClose}
        />

        <p className="add-task-modal-title">{getTranslation("newTask")}</p>

        <div className="add-task-modal-date-time-picker-container">
          <DatePicker
            style={{ width: "100%" }}
            buttonStyle={{ width: "100%" }}
            onDateSelect={setStartDate}
            toggleLabel={getTranslation("startDate")}
          />
          <TimePicker
            style={{ width: "100%" }}
            buttonStyle={{ width: "100%" }}
            onTimeSelect={setStartTime}
            toggleLabel={getTranslation("startTime")}
            quarterIntervals
          />
        </div>

        <div className="add-task-modal-date-time-picker-container">
          <DatePicker
            style={{ width: "100%" }}
            buttonStyle={{ width: "100%" }}
            onDateSelect={setEndDate}
            initialDate={endDate}
            toggleLabel={getTranslation("endDate")}
            minDate={startDate}
            resetDate={startDate}
          />
          <TimePicker
            style={{ width: "100%" }}
            buttonStyle={{ width: "100%" }}
            onTimeSelect={setEndTime}
            initialTime={endTime}
            toggleLabel={getTranslation("endTime")}
            quarterIntervals
          />
        </div>

        <AddModalInputField
          icon={<TbStack2 size={22} style={{ width: "25px" }} />}
          placeholder={getTranslation("title") + "*"}
          value={taskTitle}
          onChange={(e) => setTaskTitle(e.target.value)}
          type={"text"}
        />

        <AddModalInputField
          icon={<TbStack2 size={22} style={{ width: "25px" }} />}
          placeholder={getTranslation("subtitle")}
          value={taskSubtitle}
          onChange={(e) => setTaskSubtitle(e.target.value)}
          type={"text"}
        />

        {/* Dropdown'da subProjects listesini gösteriyoruz */}
        <Dropdown
          options={dropdownOptions}
          placeholder={getTranslation("selectProject")}
          onChange={(e) => setTaskSubProject(e.target.value)}
          value={taskSubProject}
          style={{ width: "100%" }}
        />

        <div className="add-task-modal-content">
          <IoDocumentTextOutline
            size={20}
            style={{
              width: "25px",
              alignSelf: "flex-start",
              marginTop: "10px",
            }}
          />
          <textarea
            className="add-task-modal-textarea"
            placeholder={getTranslation("description")}
            value={taskDescription}
            rows={6}
            onChange={(e) => setTaskDescription(e.target.value)}
          ></textarea>
        </div>

        <div
          style={{
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "6px",
            display: "flex",
          }}
        >
          <span
            style={{
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              display: "flex",
              gap: "5px",
            }}
          >
            <span className="add-task-modal-attachments-icon">
              <GrAttachment />
            </span>
            <span className="add-task-modal-attachments">
              {getTranslation("attachments")}
            </span>
          </span>
          <div className="add-task-modal-select-file-holder">
            <button
              className="action-button"
              onClick={() => fileInputRef.current.click()}
            >
              {getTranslation("selectFile")}
            </button>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.pdf,.txt,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rar,.zip"
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
              onChange={handleFileChange}
            />
            <span className="add-task-modal-attachments-message">
              {getTranslation("uploadDocument")}
            </span>
          </div>
          {/* Seçilen dosyaların chip'lerini gösteren alan */}
          {selectedFiles.length > 0 && (
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                gap: "8px",
                padding: "8px 0",
                width: "100%",
              }}
            >
              {selectedFiles.map((file, index) => (
                <div
                  className="add-task-modal-added-attachment-chip"
                  key={index}
                >
                  <span className="add-task-modal-added-attachment-chip-title">
                    {file.title}
                  </span>
                  <CgCloseO
                    onClick={() => removeFile(index)}
                    style={{
                      cursor: "var(--pointer)",
                      minWidth: "14px",
                      minHeight: "14px",
                      width: "14px",
                      height: "14px",
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="add-task-modal-actions">
          <button
            className="add-task-modal-button create"
            onClick={handleCreate}
          >
            {getTranslation("create")}
          </button>
        </div>
      </div>
    </div>
  );
}
